import { LanguagesIcon, LogOutIcon, SettingsIcon } from 'lucide-react';
import { useState, type FC } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover.tsx';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { useSignOut } from '../../../lib/hooks/use-sign-out.ts';
import { applyManualLanguage } from '../../../lib/i18n/i18n.ts';
import { languageConfigs, supportedLanguages } from '../../../lib/i18n/language-config.ts';
import { Can } from '../../acl/can.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { CustomerModeToggle } from '../../customer-mode/components/customer-mode-toggle.tsx';
import { ImpersonationBanner } from './impersonation-banner.tsx';
import {
    Menu,
    MenuBackRow,
    MenuDrillRow,
    MenuOptionRow,
    menuSeparatorClassName,
} from './menu-primitives.tsx';
import { PushStack } from './push-stack.tsx';
import { ViewAsRow, ViewAsRows } from './view-as-menu.tsx';

/**
 * Which setting the menu has drilled into, or `undefined` for the settings level
 * itself. Both surfaces (sidebar pop-over, mobile sheet) share these names so the
 * levels below compose the same way in each.
 */
export type SettingsSubLevel = 'language' | 'viewAs';

export type LanguageLevelProps = {
    onBack: () => void;
};

/** The pushed language level: back header + one row per supported language. */
export const LanguageLevel: FC<LanguageLevelProps> = ({ onBack }) => {
    const { t, language } = useLocale('common');
    const sendEvent = useAnalytics();

    return (
        <Menu className="p-0">
            <MenuBackRow label={t('settings__language_label')} onBack={onBack} />
            {supportedLanguages.map((supported) => (
                <MenuOptionRow
                    key={supported}
                    label={languageConfigs[supported].nativeName}
                    selected={supported === language}
                    onSelect={() => {
                        if (supported !== language) {
                            sendEvent('language_switched', {
                                from: language,
                                to: supported,
                                source: 'settings_menu',
                            });
                        }
                        applyManualLanguage(supported);
                        onBack();
                    }}
                />
            ))}
        </Menu>
    );
};

export type ViewAsLevelProps = {
    onBack: () => void;
    /** Called after an actual view switch, so the surface can dismiss entirely. */
    onSwitched: () => void;
};

/** The pushed "View as" level: back header + Manager / Sales Rep. */
export const ViewAsLevel: FC<ViewAsLevelProps> = ({ onBack, onSwitched }) => {
    const { t } = useLocale('common');

    return (
        <>
            <Menu className="p-0">
                <MenuBackRow label={t('view_switch__view_as_label')} onBack={onBack} />
            </Menu>
            <ViewAsRows onPicked={onBack} onSwitched={onSwitched} />
        </>
    );
};

export type SettingsRowsProps = {
    onOpenLevel: (level: SettingsSubLevel) => void;
    /** Called when a row completes an action that should dismiss the surface. */
    onClose?: () => void;
};

/**
 * The settings level. Customer view leads because it is the one setting reps
 * change day to day. The two drill-in settings follow as collapsed
 * "label … value" rows, and the account actions sit below a separator.
 */
export const SettingsRows: FC<SettingsRowsProps> = ({ onOpenLevel, onClose }) => {
    const { t, language } = useLocale('common');
    const { signOutFn } = useSignOut();

    return (
        <div className="flex w-full flex-col">
            <Can I="read" a="Stats" field="total_margin">
                <div className="py-tight">
                    <CustomerModeToggle />
                </div>
            </Can>

            <Menu className="p-0">
                <MenuDrillRow
                    icon={LanguagesIcon}
                    label={t('settings__language_label')}
                    value={languageConfigs[language].nativeName}
                    onOpen={() => {
                        onOpenLevel('language');
                    }}
                />

                {/* Renders nothing for non-managers. */}
                <ViewAsRow
                    onOpen={() => {
                        onOpenLevel('viewAs');
                    }}
                />
            </Menu>

            <div className={menuSeparatorClassName} />

            {/* Renders nothing unless a super-admin is impersonating. */}
            <div className="empty:hidden">
                <ImpersonationBanner expanded />
            </div>

            <Button
                variant="ghost"
                className="w-full justify-start text-content"
                leftIcon={LogOutIcon}
                onClick={() => {
                    onClose?.();
                    signOutFn();
                }}
            >
                <span className="font-normal">{t('logout__button')}</span>
            </Button>
        </div>
    );
};

/**
 * Sidebar settings entry point: everything that is not navigation or support —
 * customer view, language, the current view mode, exit demo, log out. Opens as
 * a pop-over whose contents push sideways into each setting instead of
 * stacking a second surface on top.
 *
 * The sidebar only exists at `sm` and up. Below that the mobile bottom nav
 * owns the same levels inside its own sheet.
 */
export const SettingsMenu: FC<{ expanded?: boolean }> = ({ expanded = true }) => {
    const { t } = useLocale('common');
    const sendEvent = useAnalytics();
    const [open, setOpen] = useState(false);
    const [subLevel, setSubLevel] = useState<SettingsSubLevel | undefined>(undefined);

    // Resets subLevel itself rather than relying on onOpenChange: Radix only
    // calls onOpenChange for its own interaction sources (outside click,
    // Escape, trigger), never when `open` is driven programmatically like
    // this is after a view switch — so that path needs its own reset too.
    const close = (): void => {
        setOpen(false);
        setSubLevel(undefined);
    };
    const backToSettings = (): void => {
        setSubLevel(undefined);
    };

    // Reset to the settings level on close so reopening never resumes mid-drill.
    const onOpenChange = (next: boolean): void => {
        setOpen(next);
        if (next) {
            sendEvent('settings_menu_opened', { surface: expanded ? 'sidebar' : 'rail' });
        } else {
            setSubLevel(undefined);
        }
    };

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            {expanded ? (
                <PopoverTrigger asChild>
                    <Button
                        variant="ghost"
                        className="w-full justify-start text-content"
                        leftIcon={SettingsIcon}
                    >
                        <span className="font-normal">{t('settings__button')}</span>
                    </Button>
                </PopoverTrigger>
            ) : (
                <Tooltip>
                    {/* The tooltip attaches to a wrapper rather than to the button
                        itself. Nesting TooltipTrigger asChild around PopoverTrigger
                        asChild makes the outer trigger's props win, which shadows
                        the popover's click handler and leaves the menu unopenable. */}
                    <TooltipTrigger asChild>
                        <span className="inline-flex">
                            <PopoverTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon-s"
                                    aria-label={t('settings__button')}
                                    icon={SettingsIcon}
                                />
                            </PopoverTrigger>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="right">{t('settings__button')}</TooltipContent>
                </Tooltip>
            )}
            <PopoverContent
                side="right"
                align="end"
                className="z-50 w-[260px] p-cozy"
                onOpenAutoFocus={(event) => {
                    event.preventDefault();
                    if (event.target instanceof HTMLElement) {
                        event.target.focus();
                    }
                }}
            >
                <PushStack
                    activeIndex={subLevel === undefined ? 0 : subLevel === 'language' ? 1 : 2}
                    levels={[
                        {
                            key: 'settings',
                            content: <SettingsRows onOpenLevel={setSubLevel} onClose={close} />,
                        },
                        {
                            key: 'language',
                            content: <LanguageLevel onBack={backToSettings} />,
                        },
                        {
                            key: 'viewAs',
                            content: <ViewAsLevel onBack={backToSettings} onSwitched={close} />,
                        },
                    ]}
                />
            </PopoverContent>
        </Popover>
    );
};
